import {
  defaultSchedulingCancellationPolicy,
  describeSchedulingCancellationPolicy,
  reviewSchedulingCancellationPolicy,
  type SchedulingCancellationPolicy,
  type SchedulingCancellationSettings,
  type SchedulingRetention,
} from '@hue/contracts';
import {
  Button,
  CardHeader,
  CurrencyInput,
  HelpTip,
  Input,
  Select,
  Switch,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useCompanyScope } from '../../app/company-context.js';
import { ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { getApiError, schedulingApi } from '../../lib/api.js';

const QUERY_KEY = ['company', 'scheduling', 'cancellation-policy'] as const;
const MAX_TIERS = 5;

type Target = { kind: 'COMPANY' } | { kind: 'UNIT' | 'SERVICE'; id: string };
type Remainder = 'CHOICE' | 'REFUND' | 'CREDIT';
type RetentionRule = {
  retention: SchedulingRetention;
  remainder: Remainder;
  creditValidityDays: number | null;
};

const HELP = {
  tiers:
    'Cada faixa vale para quem cancela com menos dessas horas até o início do atendimento. Com várias faixas, vale a de menos horas. Fora de qualquer faixa, o cliente recebe tudo de volta ou fica com crédito, à escolha dele.',
  hours: 'Exemplo: 24 vale para quem cancela com menos de 24 horas até o início do atendimento.',
  retentionMode:
    'Percentual do adiantamento que o cliente pagou, ou um valor fixo. A retenção nunca passa do adiantamento pago; sem adiantamento, nada é cobrado.',
  retentionAmount: 'Quanto do adiantamento fica com o estabelecimento.',
  remainder:
    'O que acontece com a parte do adiantamento que não fica retida: o cliente escolhe entre devolução e crédito, ou a regra define um dos dois.',
  noShow:
    'Quando o cliente não aparece ou avisa que não vem, a equipe registra a falta pela Agenda. Sem esta regra, a falta não gera perda automática.',
  reschedule:
    'Sem esta regra, o cliente remarca livremente. Remarcar sempre depende de a unidade confirmar o novo horário.',
  freeHours:
    'Até quantas horas antes do início o cliente remarca sem custo. Zero: sempre sem custo.',
  late: 'O que acontece quando o cliente tenta remarcar depois do prazo sem custo.',
  limit: 'Em branco, sem limite de remarcações por agendamento.',
} as const;

/// Política de cancelamento e remarcação de um nível da cascata (ADR 0071): a empresa em Dados da
/// empresa, a unidade em Unidades e o serviço no cadastro dele. Unidade e serviço podem herdar do
/// nível acima. Salvar vale para os próximos agendamentos; os existentes guardam o retrato.
export function CancellationPolicySection({
  target,
  currency = 'BRL',
  level = 2,
}: {
  target: Target;
  currency?: string | undefined;
  /// 3 dentro de um diálogo, abaixo do título dele.
  level?: 2 | 3;
}) {
  const { can, company } = useCompanyScope();
  const allowed = company.moduleKeys.includes('scheduling') && can('scheduling.settings.manage');
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: schedulingApi.cancellationPolicy,
    enabled: allowed,
    retry: false,
  });
  if (!allowed) return null;
  return (
    <section
      aria-labelledby={`cancellation-policy-${target.kind}`}
      className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-4"
    >
      <CardHeader
        id={`cancellation-policy-${target.kind}`}
        title="Cancelamento e remarcação"
        level={level}
        icon={<CalendarX2 className="size-4" />}
      />
      {query.isLoading ? (
        <LoadingCard label="Carregando a política de cancelamento" />
      ) : query.error ? (
        <ErrorCard message={getApiError(query.error)} />
      ) : (
        <PolicyForm
          key={JSON.stringify(query.data)}
          settings={query.data!}
          target={target}
          currency={currency}
        />
      )}
    </section>
  );
}

function PolicyForm({
  settings,
  target,
  currency,
}: {
  settings: SchedulingCancellationSettings;
  target: Target;
  currency: string;
}) {
  const client = useQueryClient();
  const snackbar = useSnackbar();
  const own =
    target.kind === 'COMPANY'
      ? settings.company
      : (target.kind === 'UNIT' ? settings.units : settings.services)[target.id];
  const inherited = target.kind === 'UNIT' ? settings.company : null;
  const [inherit, setInherit] = useState(target.kind !== 'COMPANY' && !own);
  const [draft, setDraft] = useState<SchedulingCancellationPolicy>(
    own ?? inherited ?? defaultSchedulingCancellationPolicy,
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const problems = inherit ? [] : reviewSchedulingCancellationPolicy(draft);

  async function save() {
    setSubmitted(true);
    if (problems.length) return;
    setSaving(true);
    setError(undefined);
    try {
      client.setQueryData(
        QUERY_KEY,
        await schedulingApi.saveCancellationPolicy(target, inherit ? null : draft),
      );
      snackbar.success(
        inherit
          ? target.kind === 'UNIT'
            ? 'Unidade usando a regra da empresa.'
            : 'Serviço usando a regra da unidade.'
          : 'Política de cancelamento salva.',
      );
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-4">
      <p className="text-sm text-content-muted">
        {target.kind === 'COMPANY'
          ? 'Vale para as unidades e os serviços sem regra própria. Cada agendamento guarda a regra em vigor quando foi feito; mudar aqui vale para os próximos.'
          : target.kind === 'UNIT'
            ? 'A unidade pode usar a regra da empresa ou ter a sua. A regra própria de um serviço vence a da unidade.'
            : 'O serviço pode usar a regra de cada unidade em que é atendido ou ter a sua, que vale em todas as unidades.'}
      </p>
      <p className="text-xs text-content-faint">
        As faixas já valem quando o cliente cancela pelo portal. A falta e a remarcação são
        registradas e mostradas ao cliente, mas ainda não retêm nada; isso vem nas próximas etapas.
      </p>
      {target.kind !== 'COMPANY' ? (
        <Switch checked={inherit} onChange={setInherit}>
          {target.kind === 'UNIT' ? 'Usar a regra da empresa' : 'Usar a regra da unidade'}
        </Switch>
      ) : null}
      {inherit ? (
        inherited ? (
          <Summary label="Regra da empresa, como o cliente lê">
            {describeSchedulingCancellationPolicy(inherited, currency)}
          </Summary>
        ) : (
          <p className="text-sm text-content-muted">
            Em cada unidade vale a regra dela, ou a da empresa quando a unidade não tem regra
            própria.
          </p>
        )
      ) : (
        <>
          <CancellationPolicyEditor value={draft} onChange={setDraft} currency={currency} />
          <Summary label="Como o cliente lê">
            {describeSchedulingCancellationPolicy(draft, currency)}
          </Summary>
        </>
      )}
      {submitted && problems.length ? (
        <ul role="alert" className="grid gap-1 text-sm text-danger">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}
      {error ? <RequestAlert message={error} /> : null}
      <div className="flex justify-end">
        <Button type="button" loading={saving} onClick={() => void save()}>
          Salvar regra de cancelamento
        </Button>
      </div>
    </div>
  );
}

function Summary({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-brand-500/20 bg-brand-500/5 p-4 text-sm leading-6">
      <p className="mb-1 text-xs font-semibold text-content/70">{label}</p>
      <p>{children}</p>
    </div>
  );
}

/// Os campos da regra: faixas de cancelamento, falta e remarcação. Cada campo tem a ajuda
/// contextual do ícone padrão (ADR 0071, decisão 9).
export function CancellationPolicyEditor({
  value,
  onChange,
  currency,
}: {
  value: SchedulingCancellationPolicy;
  onChange: (value: SchedulingCancellationPolicy) => void;
  currency: string;
}) {
  const patch = (next: Partial<SchedulingCancellationPolicy>) => onChange({ ...value, ...next });
  const reschedule = value.reschedule;
  return (
    <div className="grid gap-6">
      <fieldset className="grid gap-4">
        <legend className="mb-2 flex items-center gap-1 text-sm font-semibold">
          Faixas de cancelamento
          <HelpTip label="Ajuda: faixas de cancelamento" content={HELP.tiers} />
        </legend>
        {value.tiers.length ? null : (
          <p className="text-sm text-content-muted">
            Sem faixas, quem cancela antes do atendimento recebe tudo de volta ou fica com crédito.
          </p>
        )}
        {value.tiers.map((tier, index) => (
          <div
            key={index}
            role="group"
            aria-label={`Faixa ${String(index + 1)}`}
            className="grid gap-3 rounded-xl border border-surface-border p-3"
          >
            <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-end">
              <Input
                label="Com menos de (horas)"
                labelAddon={<HelpTip label="Ajuda: com menos de (horas)" content={HELP.hours} />}
                type="number"
                min={1}
                step="1"
                value={tier.withinHours || ''}
                onChange={(event) =>
                  patch({
                    tiers: value.tiers.map((item, at) =>
                      at === index ? { ...item, withinHours: Number(event.target.value) } : item,
                    ),
                  })
                }
              />
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => patch({ tiers: value.tiers.filter((_, at) => at !== index) })}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                  Remover faixa
                </Button>
              </div>
            </div>
            <RetentionFields
              value={tier}
              currency={currency}
              onChange={(rule) =>
                patch({
                  tiers: value.tiers.map((item, at) =>
                    at === index ? { ...item, ...rule } : item,
                  ),
                })
              }
            />
          </div>
        ))}
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={value.tiers.length >= MAX_TIERS}
            onClick={() =>
              patch({
                tiers: [
                  ...value.tiers,
                  {
                    withinHours: value.tiers.length
                      ? Math.max(1, Math.min(...value.tiers.map((tier) => tier.withinHours)) - 1)
                      : 24,
                    retention: { mode: 'PERCENTAGE', amount: 10000 },
                    remainder: 'CHOICE',
                    creditValidityDays: null,
                  },
                ],
              })
            }
          >
            <Plus className="size-4" aria-hidden="true" />
            Adicionar faixa
          </Button>
        </div>
      </fieldset>

      <fieldset className="grid gap-4">
        <legend className="mb-2 flex items-center gap-1 text-sm font-semibold">
          Falta
          <HelpTip label="Ajuda: falta" content={HELP.noShow} />
        </legend>
        <Switch
          checked={value.noShow !== null}
          onChange={(on) =>
            patch({
              noShow: on
                ? {
                    retention: { mode: 'PERCENTAGE', amount: 10000 },
                    remainder: 'CHOICE',
                    creditValidityDays: null,
                  }
                : null,
            })
          }
        >
          Reter parte do adiantamento quando o cliente faltar
        </Switch>
        {value.noShow ? (
          <RetentionFields
            value={value.noShow}
            currency={currency}
            onChange={(rule) => patch({ noShow: { ...value.noShow!, ...rule } })}
          />
        ) : null}
      </fieldset>

      <fieldset className="grid gap-4">
        <legend className="mb-2 flex items-center gap-1 text-sm font-semibold">
          Remarcação
          <HelpTip label="Ajuda: remarcação" content={HELP.reschedule} />
        </legend>
        <Switch
          checked={reschedule !== null}
          onChange={(on) =>
            patch({
              reschedule: on
                ? { freeHours: 24, late: 'CONTACT', retention: null, limit: null }
                : null,
            })
          }
        >
          Definir prazo e limite para remarcar
        </Switch>
        {reschedule ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Remarcar sem custo até (horas antes)"
              labelAddon={<HelpTip label="Ajuda: remarcar sem custo" content={HELP.freeHours} />}
              type="number"
              min={0}
              step="1"
              value={reschedule.freeHours}
              onChange={(event) =>
                patch({
                  reschedule: { ...reschedule, freeHours: Math.max(0, Number(event.target.value)) },
                })
              }
            />
            <Select
              label="Depois do prazo"
              labelAddon={<HelpTip label="Ajuda: depois do prazo" content={HELP.late} />}
              value={reschedule.late}
              options={[
                { value: 'CONTACT', label: 'Falar com o estabelecimento' },
                { value: 'RETAIN', label: 'Reter parte do adiantamento' },
                { value: 'FREE', label: 'Remarcar sem custo' },
              ]}
              onValueChange={(late) =>
                patch({
                  reschedule: {
                    ...reschedule,
                    late: late as 'CONTACT' | 'RETAIN' | 'FREE',
                    retention:
                      late === 'RETAIN'
                        ? (reschedule.retention ?? { mode: 'PERCENTAGE', amount: 5000 })
                        : null,
                  },
                })
              }
            />
            {reschedule.late === 'RETAIN' && reschedule.retention ? (
              <AmountFields
                retention={reschedule.retention}
                currency={currency}
                onChange={(retention) => patch({ reschedule: { ...reschedule, retention } })}
              />
            ) : null}
            <Input
              label="Limite de remarcações"
              labelAddon={<HelpTip label="Ajuda: limite de remarcações" content={HELP.limit} />}
              type="number"
              min={1}
              max={20}
              step="1"
              value={reschedule.limit ?? ''}
              onChange={(event) =>
                patch({
                  reschedule: {
                    ...reschedule,
                    limit: event.target.value === '' ? null : Number(event.target.value),
                  },
                })
              }
            />
          </div>
        ) : null}
      </fieldset>
    </div>
  );
}

function RetentionFields({
  value,
  onChange,
  currency,
}: {
  value: RetentionRule;
  onChange: (value: RetentionRule) => void;
  currency: string;
}) {
  const whole = value.retention.mode === 'PERCENTAGE' && value.retention.amount >= 10000;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <AmountFields
        retention={value.retention}
        currency={currency}
        onChange={(retention) => onChange({ ...value, retention })}
      />
      {whole ? null : (
        <Select
          label="O restante"
          labelAddon={<HelpTip label="Ajuda: o restante" content={HELP.remainder} />}
          value={value.remainder}
          options={[
            { value: 'CHOICE', label: 'O cliente escolhe' },
            { value: 'REFUND', label: 'Devolução' },
            { value: 'CREDIT', label: 'Crédito' },
          ]}
          onValueChange={(remainder) => onChange({ ...value, remainder: remainder as Remainder })}
        />
      )}
    </div>
  );
}

function AmountFields({
  retention,
  onChange,
  currency,
}: {
  retention: SchedulingRetention;
  onChange: (retention: SchedulingRetention) => void;
  currency: string;
}) {
  return (
    <>
      <Select
        label="Retenção"
        labelAddon={<HelpTip label="Ajuda: retenção" content={HELP.retentionMode} />}
        value={retention.mode}
        options={[
          { value: 'PERCENTAGE', label: 'Percentual do adiantamento' },
          { value: 'FIXED', label: 'Valor fixo' },
        ]}
        onValueChange={(mode) =>
          onChange({
            mode: mode as SchedulingRetention['mode'],
            amount: mode === 'PERCENTAGE' ? 10000 : 0,
          })
        }
      />
      {retention.mode === 'PERCENTAGE' ? (
        <Input
          label="Percentual retido (%)"
          labelAddon={<HelpTip label="Ajuda: percentual retido" content={HELP.retentionAmount} />}
          type="number"
          min={0.01}
          max={100}
          step="0.01"
          value={retention.amount ? retention.amount / 100 : ''}
          onChange={(event) =>
            onChange({
              ...retention,
              amount: event.target.value === '' ? 0 : Math.round(Number(event.target.value) * 100),
            })
          }
        />
      ) : (
        <CurrencyInput
          label="Valor retido"
          labelAddon={<HelpTip label="Ajuda: valor retido" content={HELP.retentionAmount} />}
          currency={currency}
          value={retention.amount || null}
          onValueChange={(amount) => onChange({ ...retention, amount: amount ?? 0 })}
        />
      )}
    </>
  );
}
